import { Box, Divider, Typography, useTheme } from "@mui/material";
import SecondaryBox from "../../../atoms/box/SecondaryBox";
import { NavbarIcon } from "../../../atoms/navbar/Icon";
import { DefaultBadge, StatusBadge } from "../../../atoms/StatusBadge";
import { navigateToRunnableInNewTab } from "../../../components/RunnableNavigator";
import { SmallModal } from "../../../components/SmallModal";
import { openSnackbar } from "../../../context/SnackbarContext";
import { useDeployment } from "../../../hooks/useDeployments";
import { useOrganizationSecret } from "../../../hooks/useOrganizationSecrets";
import { useOrganizationVariable } from "../../../hooks/useOrganizationVariables";
import { DeploymentPermission, DeploymentStatus } from "../../../models/types";
import { RoleReadOnlyCustomField } from "../../../molecules/custom-fields/RoleCustomField";
import { EmptyTable } from "../../../molecules/EmptyTable";
import { JSONTableView } from "../../../molecules/JSONTableView";
import { OrganizationSecretDisplay } from "../../../molecules/OrganizationSecretDisplay";
import { ShowMore } from "../../../molecules/ShowMore";
import { RectangularLoader } from "../../../molecules/skeleton-loader/RectangularLoader";
import { TableTextWithEllipsis } from "../../../molecules/TableTextWithEllipsis";
import {
  DeploymentPermissionLabels,
  getBaseDeploymentUrl,
} from "../../../utilities/DeploymentHelper";
import { defaultDateDisplay } from "../../../utilities/UIHelper";
import {
  GuardrailsPIISettings,
  GuardrailsToxicitySettings,
  GuardrailsProfanitySettings,
  GuardrailSetting,
  DeploymentDetailsSection,
} from "./CreateDeploymentModal";
import { useEffect, useState } from "react";
import { IconButton } from "../../../atoms/IconButton";
import { LoadingButton } from "@mui/lab";
import { editDeployment } from "../../../services/DeploymentService";

type DeploymentDetailsModalProps = {
  /** Should the modal be open? */
  open: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  deploymentId: string;
};

type RenderVariableProps = {
  key: string;
  row: {
    key: string;
    secret: boolean;
    value: string;
  };
};

const RenderVariableValue = ({ key, row }: RenderVariableProps) => {
  const { data: variableObj, isLoading: loadingVar } = useOrganizationVariable(
    row.secret ? "" : row.value,
    {}
  );
  const { data: secretObj, isLoading: loadingSecret } = useOrganizationSecret(
    row.secret ? row.value : "",
    {}
  );
  const isLoading = loadingSecret || loadingVar;
  const isKeyColumn = key === "key";
  return isLoading ? (
    <RectangularLoader width="200px" height="20px" />
  ) : (
    <Box display="flex">
      {isKeyColumn && <TableTextWithEllipsis value={row.key} />}
      {!isKeyColumn && secretObj && <OrganizationSecretDisplay value={secretObj} />}
      {!isKeyColumn && variableObj && (
        <DefaultBadge value={`${variableObj.name}: ${variableObj.value}`} />
      )}
    </Box>
  );
};

type GuardrailsReadOnlyViewProps = {
  guardrails_settings?: {
    pii_general: boolean;
    pii_finance: boolean;
    pii_it: boolean;
    pii_usa_specific: boolean;
    pii_canada_specific: boolean;
    toxicity_low: boolean;
    toxicity_medium: boolean;
    toxicity_high: boolean;
    profanity: boolean;
  };
};

const GuardrailsReadOnlyView = ({ guardrails_settings }: GuardrailsReadOnlyViewProps) => {
  if (!guardrails_settings) {
    return <EmptyTable variant="organization" label="No guardrails configured" />;
  }

  // Convert the guardrails_settings to the format expected by the components
  const hasPiiSettings =
    guardrails_settings.pii_general ||
    guardrails_settings.pii_finance ||
    guardrails_settings.pii_it ||
    guardrails_settings.pii_usa_specific ||
    guardrails_settings.pii_canada_specific;

  const toxicityLevel = guardrails_settings.toxicity_low
    ? "low"
    : guardrails_settings.toxicity_medium
    ? "medium"
    : guardrails_settings.toxicity_high
    ? "high"
    : "high";

  const hasToxicity =
    guardrails_settings.toxicity_low ||
    guardrails_settings.toxicity_medium ||
    guardrails_settings.toxicity_high;

  return (
    <Box display="flex" flexDirection="column" gap="10px">
      <Typography variant="body2">
        We only apply guardrails to user inputs and final outputs, and all filtering actions are
        fully logged in the trace — nothing is hidden.
      </Typography>
      <GuardrailsPIISettings
        generalActive={guardrails_settings.pii_general}
        setGeneralActive={() => {}} // Disabled
        financeActive={guardrails_settings.pii_finance}
        setFinanceActive={() => {}} // Disabled
        itActive={guardrails_settings.pii_it}
        setItActive={() => {}} // Disabled
        USAActive={guardrails_settings.pii_usa_specific}
        setUSAActive={() => {}} // Disabled
        canadaActive={guardrails_settings.pii_canada_specific}
        setCanadaActive={() => {}} // Disabled
        active={hasPiiSettings}
        setActive={() => {}} // Disabled
        readOnly={true}
      />
      <GuardrailsToxicitySettings
        option={toxicityLevel as "low" | "medium" | "high"}
        setOption={() => {}} // Disabled
        active={hasToxicity}
        setActive={() => {}} // Disabled
        readOnly={true}
      />
      <GuardrailsProfanitySettings
        active={guardrails_settings.profanity}
        setActive={() => {}} // Disabled
        readOnly={true}
      />
    </Box>
  );
};

export const DeploymentDetailsModal = (props: DeploymentDetailsModalProps) => {
  const { open, onClose, deploymentId } = props;
  const { data: deployment, isLoading } = useDeployment(deploymentId);
  const deploymentEndpoint = `${getBaseDeploymentUrl()}${deployment?.id}`;
  const theme = useTheme();
  const [editing, setEditing] = useState<boolean>(false);

  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [readPermission, setReadPermission] = useState<DeploymentPermission>(
    DeploymentPermission.EVERYONE
  );
  const [editPermission, setEditPermission] = useState<DeploymentPermission>(
    DeploymentPermission.EVERYONE
  );
  const [readRoles, setReadRoles] = useState<string[] | null>([]);
  const [editRoles, setEditRoles] = useState<string[] | null>([]);
  const [status, setStatus] = useState<DeploymentStatus>("active");

  const handleSave = async () => {
    try {
      await editDeployment(deploymentId, {
        name,
        description,
        read_permission: readPermission,
        edit_permission: editPermission,
        edit_authorized_roles: editRoles ?? [],
        read_authorized_roles: readRoles ?? [],
        status: status,
      });
      openSnackbar("Deployment updated successfully", "success");
      setEditing(false);
    } catch (e) {
      openSnackbar("Failed to save deployment", "error");
    }
  };

  useEffect(() => {
    if (deployment) {
      setName(deployment.name);
      setDescription(deployment.description);
      setReadPermission(deployment.read_permission);
      setEditPermission(deployment.edit_permission);
      setReadRoles(deployment.read_authorized_roles ?? []);
      setEditRoles(deployment.edit_authorized_roles ?? []);
      setStatus(deployment.status);
    }
  }, [deployment]);

  return (
    <SmallModal
      open={open}
      onClose={onClose}
      size="medium"
      position="right"
      isLoading={isLoading}
      title={editing ? "Edit Deployment" : deployment?.name}
      customTitle={
        editing && (
          <Box display="flex" flexDirection="row" gap="10px" alignItems="center">
            <Box
              onClick={() => setEditing(false)}
              sx={{ cursor: "pointer" }}
              display="flex"
              alignItems="center"
            >
              <NavbarIcon
                variant="left-arrow"
                sx={{ width: "22px", minWidth: "22px", height: "22px", minHeight: "22px" }}
              />
            </Box>
            <Typography variant="h2">Edit Deployment</Typography>
          </Box>
        )
      }
      rightComponent={
        !editing && (
          <IconButton
            color={theme.palette.custom.hyperlink}
            height="15px"
            width="15px"
            onClick={() => setEditing(true)}
            variant="edit-icon"
          />
        )
      }
      action={
        editing && (
          <LoadingButton
            variant="contained"
            onClick={handleSave}
            fullWidth
            sx={{
              margin: "0px 10px 10px 10px",
            }}
          >
            Save
          </LoadingButton>
        )
      }
    >
      {editing ? (
        <DeploymentDetailsSection
          readRoles={readRoles}
          setReadRoles={setReadRoles}
          editRoles={editRoles}
          setEditRoles={setEditRoles}
          editPermission={editPermission}
          setEditPermission={setEditPermission}
          readPermission={readPermission}
          setReadPermission={setReadPermission}
          name={name}
          setName={setName}
          description={description}
          setDescription={setDescription}
          status={status}
          setStatus={setStatus}
        />
      ) : deployment ? (
        <Box display="flex" flexDirection="column" gap="16px">
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            width="100%"
            alignItems="center"
          >
            <Typography variant="h3">ID</Typography>
            <TableTextWithEllipsis fullWidth={false} value={deployment.id} />
          </Box>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            width="100%"
            alignItems="center"
          >
            <Typography variant="h3">Runnable</Typography>
            <DefaultBadge
              value={`${deployment?.runnable_name.name}-${deployment?.runnable_name.version}`}
              onClick={() => {
                navigateToRunnableInNewTab(deployment.runnable_name);
              }}
            />
          </Box>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            width="100%"
            alignItems="center"
          >
            <Typography variant="h3">Status</Typography>
            <StatusBadge
              justifyContent="flex-end"
              status={deployment?.status?.toUpperCase() as "ACTIVE" | "INACTIVE"}
            />
          </Box>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            width="100%"
            alignItems="center"
          >
            <Typography variant="h3">Deployed On</Typography>
            <Typography variant="body2">{defaultDateDisplay(deployment?.created_on)}</Typography>
          </Box>
          <Typography variant="h3">Endpoint</Typography>
          <Box
            display="flex"
            flexDirection="row"
            width="100%"
            gap="5px"
            sx={{
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.stopPropagation();
              navigator.clipboard.writeText(deploymentEndpoint);
              openSnackbar("Endpoint copied successfully", "success");
            }}
          >
            <TableTextWithEllipsis value={deploymentEndpoint} expanded />
            <NavbarIcon
              color={theme.palette.custom.hyperlink}
              variant="copy"
              sx={{ height: "18px", width: "18px" }}
            />
          </Box>
          {deployment.description !== "" && (
            <>
              <Typography variant="h3">Description</Typography>
              <ShowMore richText description={deployment.description} />
            </>
          )}
          <Typography variant="h3">Visibility</Typography>
          <SecondaryBox>
            <Box display="flex" flexDirection="row" gap="10px" width="100%">
              <Box display="flex" flexDirection="column" gap="10px" flexGrow={1} maxWidth="50%">
                <Typography variant="h3">Access</Typography>
                <DefaultBadge value={DeploymentPermissionLabels[deployment.read_permission]} />
                {deployment.read_permission === DeploymentPermission.SPECIFIC_ROLES && (
                  <>
                    <Typography variant="h3">Access Roles</Typography>
                    <RoleReadOnlyCustomField value={deployment.read_authorized_roles ?? []} />
                  </>
                )}
              </Box>
              <Box display="flex" flexDirection="column" gap="10px" flexGrow={1} maxWidth="50%">
                <Typography variant="h3">Edit</Typography>
                <DefaultBadge value={DeploymentPermissionLabels[deployment.edit_permission]} />
                {deployment.edit_permission === DeploymentPermission.SPECIFIC_ROLES && (
                  <>
                    <Typography variant="h3">Edit Roles</Typography>
                    <RoleReadOnlyCustomField value={deployment.edit_authorized_roles ?? []} />
                  </>
                )}
              </Box>
            </Box>
          </SecondaryBox>
          <Typography variant="h3">Variables and Secrets</Typography>
          <Box>
            {deployment?.variables_mapping?.length > 0 && (
              <JSONTableView
                customRender={RenderVariableValue}
                hiddenColumns={["secret"]}
                json={JSON.stringify(deployment.variables_mapping ?? {})}
              />
            )}
            {deployment?.variables_mapping?.length === 0 && (
              <EmptyTable variant="organization" label="No variables and secrets" />
            )}
          </Box>
          <Typography variant="h3">Guardrails Settings</Typography>
          <SecondaryBox>
            <GuardrailsReadOnlyView guardrails_settings={deployment?.guardrails_settings} />
          </SecondaryBox>
        </Box>
      ) : null}
    </SmallModal>
  );
};
